import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'defaultAssistiveLabel'
	| 'specialistAssistiveLabel'
	| 'deepResearchAssistiveLabel'
	| 'deletedAgentIconLabel'
	| 'deletedAgent'
	| 'deletedAgentStartNewChatButton'
	| 'dictationListeningLabel'
	| 'dictationTranscribingLabel'
	| 'agentRoutingFailed'
	| 'agentRoutingResolving'
	| 'mentionAgentsSectionSwitch'
	| 'rovoMentionName'
	| 'agentMentionsOnboardingSpotlightTitle'
	| 'agentMentionsOnboardingSpotlightBody'
	| 'agentMentionsOnboardingSpotlightOk'
	| 'agentMentionsOnboardingSpotlightDismissLabel'
	| 'skillsOnboardingSpotlightTitle'
	| 'skillsOnboardingSpotlightBody'
	| 'skillsOnboardingSpotlightViewSkills';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		defaultAssistiveLabel: {
			id: 'ai-mate.chat-input.chat-input-start-assistive-label',
			defaultMessage: 'Write a prompt, @mentions someone, or use / for actions',
			description:
				'Accessibility label (aria-label) for the chat input text field in the AI conversation interface. Shown as a screen reader announcement when the input field is focused, providing instructions on what users can do in the input. The purpose is to guide users on available input options: writing prompts, mentioning people using @ symbol, or accessing actions using the / command. This helps users with assistive technologies understand the input field capabilities.',
		},
		specialistAssistiveLabel: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.specialistAssistiveLabel',
			defaultMessage: 'Describe what to do, or / for actions',
			description: 'Placeholder for the chat input when a specialist agent is selected.',
		},
		deepResearchAssistiveLabel: {
			id: 'ai-mate.chat-input.chat-input-deep-research-assistive-label',
			defaultMessage: 'Get a detailed report',
			description:
				'Accessibility label (aria-label) for the chat input text field when deep research mode is active in the AI conversation interface. Shown as a screen reader announcement when the input field is focused in deep research mode. The purpose is to inform users with assistive technologies that they are in deep research mode, which will provide more thorough and detailed responses. This helps users understand the enhanced capabilities available in this mode.',
		},
		deletedAgentIconLabel: {
			id: 'ai-mate.chat-input.deleted-agent-icon-label',
			defaultMessage: 'Agent deleted',
			description:
				'Accessibility label (aria-label) for an icon or visual indicator displayed when the AI agent associated with the current conversation has been deleted. Shown as a screen reader announcement when the icon is focused or hovered. The purpose is to provide accessible information about the agent deletion status for users with assistive technologies, indicating that the agent is no longer available.',
		},
		deletedAgent: {
			id: 'ai-mate.chat-input.deleted-agent-warning',
			defaultMessage: 'This Agent has been deleted.',
			description:
				'Warning message displayed in a notification, alert, or banner when a user attempts to interact with a conversation that was associated with an AI agent that has since been deleted. Shown as a warning message to inform the user that the agent is no longer available. The purpose is to clearly communicate why the conversation cannot continue with the original agent and guide the user to start a new conversation.',
		},
		deletedAgentStartNewChatButton: {
			id: 'ai-mate.chat-input.deleted-agent-start-new-chat',
			defaultMessage: 'Start new chat',
			description:
				'Button label displayed on a button that appears when the AI agent has been deleted, allowing users to begin a new conversation. Shown as the text on a button in a warning dialog or banner. When clicked, it clears the current conversation context and starts a fresh chat session. The purpose is to provide users with a clear path forward when their previous agent is no longer available.',
		},
		dictationListeningLabel: {
			id: 'ai-mate.chat-input.dictation-listening-label',
			defaultMessage: 'Listening…',
			description:
				'Status label displayed over the chat input editor while voice dictation is actively recording audio.',
		},
		dictationTranscribingLabel: {
			id: 'ai-mate.chat-input.dictation-transcribing-label',
			defaultMessage: 'Transcribing',
			description:
				'Status label displayed over the chat input editor while recorded audio is being converted to text.',
		},
		agentRoutingFailed: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.agentRoutingFailed',
			defaultMessage: 'Agent unavailable. Remove the mention and select the agent again.',
			description:
				'Tooltip and accessibility label for the disabled send button when a mentioned agent could not be resolved for message routing.',
		},
		agentRoutingResolving: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.agentRoutingResolving',
			defaultMessage: 'Resolving mentioned agent…',
			description:
				'Tooltip and accessibility label for the disabled send button while a mentioned agent is being resolved for message routing.',
		},
		mentionAgentsSectionSwitch: {
			id: 'ai-mate.chat-input.mention-agents-section-switch',
			defaultMessage: 'Switch to',
			description:
				'Section heading shown above the list of AI agents in the @-mention type-ahead menu of the chat input, displayed when an agent is already mentioned in the conversation. Indicates that selecting an agent from the list will switch the active agent for the conversation.',
		},
		rovoMentionName: {
			id: 'ai-mate.chat-input.rovo-mention-name',
			defaultMessage: 'Rovo',
			description:
				'Name of the synthetic default-Rovo entry in the @-mention type-ahead, shown when an agent is active so the user can @-mention Rovo to route the next message back to general Rovo chat. "Rovo" is a brand name and stays the same across languages.',
		},
		agentMentionsOnboardingSpotlightTitle: {
			id: 'ai-mate.chat-input.agent-mentions-onboarding-spotlight-title',
			defaultMessage: 'Invoke agents with @Mentions',
			description:
				'Headline text shown in the onboarding spotlight that appears when opening Rovo Chat and agent mentions are enabled.',
		},
		agentMentionsOnboardingSpotlightBody: {
			id: 'ai-mate.chat-input.agent-mentions-onboarding-spotlight-body',
			defaultMessage:
				'Bring agents into your workflow instantly. Type @ to select an agent and include it in your chat',
			description:
				'Body text shown in the onboarding spotlight that introduces agent mentions in Rovo Chat.',
		},
		agentMentionsOnboardingSpotlightOk: {
			id: 'ai-mate.chat-input.agent-mentions-onboarding-spotlight-ok',
			defaultMessage: 'OK',
			description: 'Primary action label for dismissing the agent mentions onboarding spotlight.',
		},
		agentMentionsOnboardingSpotlightDismissLabel: {
			id: 'ai-mate.chat-input.agent-mentions-onboarding-spotlight-dismiss-label',
			defaultMessage: 'Dismiss agent mentions onboarding spotlight',
			description:
				'Accessibility label for dismissing the agent mentions onboarding spotlight using the close control.',
		},
		skillsOnboardingSpotlightTitle: {
			id: 'ai-mate.chat-input.skills-onboarding-spotlight-title',
			defaultMessage: 'Type / to try a skill',
			description:
				'Headline text shown in the onboarding spotlight that appears when opening Rovo Chat for the first time and the user has not used a skill before.',
		},
		skillsOnboardingSpotlightBody: {
			id: 'ai-mate.chat-input.skills-onboarding-spotlight-body',
			defaultMessage:
				'Skills help with specialized workflows and can be assigned to agents. Type / in chat to explore them all.',
			description:
				'Body text shown in the onboarding spotlight that introduces skills in Rovo Chat.',
		},
		skillsOnboardingSpotlightViewSkills: {
			id: 'ai-mate.chat-input.skills-onboarding-spotlight-view-skills',
			defaultMessage: 'View skills',
			description:
				'Primary action label for the skills onboarding spotlight. Clicking it inserts / into the chat input and opens the skill picker.',
		},
	});

export default message;
